import { useEffect, useMemo, useState } from "react";
import { CalendarDays, FolderKanban, MapPin, RefreshCw, Search, UserRound } from "lucide-react";
import api from "../../services/api";

const DAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const PERIOD_OPTIONS = [
  { key: "day", label: "Dag" },
  { key: "week", label: "Week" },
  { key: "month", label: "Maand" },
  { key: "year", label: "Jaar" },
];

export default function ProjectWeekOverviewPage() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    projectCount: 0,
    workOrderCount: 0,
    monteurCount: 0,
    laborHours: 0,
    laborCostExcl: 0,
    laborRevenueExcl: 0,
    laborMarginExcl: 0,
    materialUsedExcl: 0,
    materialOrderedExcl: 0,
    materialOrderedByMonteurExcl: 0,
    materialOrderCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => toISODate(new Date()));
  const [granularity, setGranularity] = useState("week");
  const [periodMeta, setPeriodMeta] = useState({ weekNumber: null, month: null, year: null });

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        setLoading(true);
        setError("");
        const summaryRes = await api.get("/projects/week-summary", {
          params: {
            date: selectedDate,
            granularity,
          },
        });

        if (!mounted) return;
        setRows(Array.isArray(summaryRes?.data?.rows) ? summaryRes.data.rows : []);
        setTotals(summaryRes?.data?.totals || {});
        setPeriodMeta({
          weekNumber: summaryRes?.data?.weekNumber ?? null,
          month: summaryRes?.data?.month ?? null,
          year: summaryRes?.data?.year ?? null,
        });
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || "Projectweekoverzicht laden mislukt");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, [selectedDate, granularity]);

  const weekDates = useMemo(() => buildWeek(selectedDate), [selectedDate]);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredRows = useMemo(
    () =>
      rows
      .filter((row) => {
        if (!normalizedQuery) return true;
        return [row.projectNummer, row.projectNaam, row.clientName, row.locationText, (row.monteurNames || []).join(" ")]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        if (b.workOrderCount !== a.workOrderCount) return b.workOrderCount - a.workOrderCount;
        return String(a.projectNaam || "").localeCompare(String(b.projectNaam || ""), "nl-NL");
      }),
    [rows, normalizedQuery]
  );

  const periodTitle = useMemo(() => {
    if (granularity === "day") return `Dag ${selectedDate}`;
    if (granularity === "month") return `Maand ${String(periodMeta.month || "").padStart(2, "0")}-${periodMeta.year || ""}`;
    if (granularity === "year") return `Jaar ${periodMeta.year || ""}`;
    return `Week ${periodMeta.weekNumber || getWeekNumber(selectedDate)}`;
  }, [granularity, periodMeta.month, periodMeta.weekNumber, periodMeta.year, selectedDate]);

  return (
    <section className="space-y-6">
      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(190,242,100,0.45),_transparent_32%),linear-gradient(135deg,_#0f172a,_#1e293b_55%,_#334155)] p-6 text-white shadow-xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-lime-200">
              Projectweekoverzicht
            </p>
            <h2 className="text-3xl font-semibold tracking-tight">
              {periodTitle}: inzet, kosten, uren en materiaal.
            </h2>
            <p className="max-w-2xl text-sm text-slate-200">
              Kies dag, week, maand of jaar en zie per project direct bezetting, arbeid en materiaal.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:min-w-[420px] lg:grid-cols-4">
            <MetricCard label="Projecten" value={Number(totals.projectCount || 0)} icon={FolderKanban} />
            <MetricCard label="Werkbonnen" value={Number(totals.workOrderCount || 0)} icon={CalendarDays} />
            <MetricCard label="Monteurs" value={Number(totals.monteurCount || 0)} icon={UserRound} />
            <MetricCard label="Arbeidsuren" value={Number(totals.laborHours || 0).toFixed(1)} icon={RefreshCw} />
          </div>
        </div>
      </div>

      <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Weekfocus</span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Zoeken</span>
              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Project, klant, monteur..."
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </div>
            </label>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {PERIOD_OPTIONS.map((option) => {
                const active = granularity === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setGranularity(option.key)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                      active
                        ? "bg-slate-900 text-white"
                        : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {granularity === "week" && (
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                {weekDates.map((date) => {
                  const active = date.iso === selectedDate;
                  return (
                    <button
                      key={date.iso}
                      type="button"
                      onClick={() => setSelectedDate(date.iso)}
                      className={`rounded-2xl px-3 py-2 text-left transition ${
                        active ? "bg-slate-900 text-white shadow" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <div className="text-xs font-medium">{date.dayName}</div>
                      <div className="text-sm font-semibold">{date.dayNumber}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {!loading && !error && (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <ValueCard label="Arbeid kost excl" value={`€ ${toMoney(totals.laborCostExcl || 0)}`} />
          <ValueCard label="Arbeid omzet excl" value={`€ ${toMoney(totals.laborRevenueExcl || 0)}`} />
          <ValueCard label="Arbeid marge excl" value={`€ ${toMoney(totals.laborMarginExcl || 0)}`} />
          <ValueCard label="Materiaal gebruikt excl" value={`€ ${toMoney(totals.materialUsedExcl || 0)}`} />
          <ValueCard label="Materiaal besteld excl" value={`€ ${toMoney(totals.materialOrderedExcl || 0)}`} />
          <ValueCard label="Besteld door monteurs" value={`€ ${toMoney(totals.materialOrderedByMonteurExcl || 0)}`} />
          <ValueCard label="Bestelorders materiaal" value={Number(totals.materialOrderCount || 0)} />
          <ValueCard label="Resultaat zichtbaar in" value={periodTitle} />
        </section>
      )}

      {loading && (
        <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          Projectweekoverzicht laden...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow-sm">
          {error}
        </div>
      )}

      {!loading && !error && (
        <section className="space-y-4">
          {filteredRows.map((row) => (
            <article key={row.projectId} className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-4 border-b border-slate-100 bg-slate-50/80 px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                    {row.projectNummer}
                  </p>
                  <h3 className="text-lg font-semibold text-slate-950">{row.projectNaam}</h3>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                    <InlineInfo icon={UserRound} text={row.clientName} />
                    <InlineInfo icon={MapPin} text={row.locationText} />
                    <InlineInfo icon={CalendarDays} text={`${row.workOrderCount || 0} werkbon(nen)`} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                    {Number(row.laborHours || 0).toFixed(1)} uur
                  </span>
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                    Arbeid kost € {toMoney(row.laborCostExcl || 0)}
                  </span>
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    Omzet € {toMoney((row.realizedRevenueExcl || 0))}
                  </span>
                </div>
              </div>

              <div className="grid gap-4 p-5 lg:grid-cols-[280px_minmax(0,1fr)]">
                <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Monteurs in deze periode
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(row.monteurNames || []).length > 0 ? (
                      row.monteurNames.map((name) => (
                        <span key={name} className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-sm font-medium text-sky-700">
                          {name}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-slate-500">Nog geen monteur bekend.</span>
                    )}
                  </div>
                  <div className="mt-4 space-y-1 text-sm text-slate-700">
                    <p>Arbeid omzet: € {toMoney(row.laborRevenueExcl || 0)}</p>
                    <p>Arbeid marge: € {toMoney(row.laborMarginExcl || 0)}</p>
                    <p>Materiaal gebruikt: € {toMoney(row.materialUsedExcl || 0)}</p>
                    <p>Materiaal besteld: € {toMoney(row.materialOrderedExcl || 0)}</p>
                  </div>
                </div>

                <div className="rounded-[20px] border border-slate-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Werkbonnen in deze periode
                  </p>
                  <div className="mt-3 space-y-3">
                    {(row.workOrders || []).map((workOrder) => (
                      <a
                        key={workOrder.id}
                        href={`/werkbon/${workOrder.id}`}
                        className="flex items-start justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-3 transition hover:bg-slate-100"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            {workOrder.werkbonNummer}
                          </p>
                          <p className="truncate text-sm font-medium text-slate-900">{workOrder.title}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {formatShortDate(workOrder.plannedDate)} {workOrder.startTime || ""}
                          </p>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-slate-600">
                          {(workOrder.assignedNames || []).join(", ") || "Nog niet toegewezen"}
                        </span>
                      </a>
                    ))}
                    {(row.workOrders || []).length === 0 && (
                      <p className="text-sm text-slate-500">Geen werkbonnen gepland in deze periode.</p>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}

          {filteredRows.length === 0 && (
            <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
              Geen projectinzet gevonden in deze week.
            </div>
          )}
        </section>
      )}
    </section>
  );
}

function MetricCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-[20px] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.22em] text-slate-200">{label}</span>
        <Icon size={16} className="text-lime-200" />
      </div>
      <p className="mt-3 text-3xl font-semibold">{value}</p>
    </div>
  );
}

function ValueCard({ label, value }) {
  return (
    <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-2 text-lg font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function InlineInfo({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className="shrink-0 text-slate-400" />
      <span>{text}</span>
    </div>
  );
}

function buildWeek(selectedDate) {
  const base = selectedDate ? new Date(`${selectedDate}T12:00:00`) : new Date();
  const day = base.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  const monday = new Date(base);
  monday.setDate(base.getDate() + offset);

  return DAYS.map((dayName, index) => {
    const current = new Date(monday);
    current.setDate(monday.getDate() + index);
    return { dayName, dayNumber: String(current.getDate()).padStart(2, "0"), iso: toISODate(current) };
  });
}

function getWeekNumber(selectedDate) {
  const date = selectedDate ? new Date(`${selectedDate}T12:00:00`) : new Date();
  const current = new Date(date.getTime());
  current.setHours(0, 0, 0, 0);
  current.setDate(current.getDate() + 4 - (current.getDay() || 7));
  const yearStart = new Date(current.getFullYear(), 0, 1);
  return Math.ceil((((current - yearStart) / 86400000) + 1) / 7);
}

function toISODate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatShortDate(value) {
  const safe = value || toISODate(new Date());
  const date = new Date(`${safe}T12:00:00`);
  return date.toLocaleDateString("nl-NL", { day: "2-digit", month: "short" });
}

function toMoney(value) {
  const numeric = Number(value || 0);
  return numeric.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
